import { FC, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/helper/api";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import { useTranslation } from "react-i18next";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import { KAER_COMPANY_SUBJECT_ID } from "@/helper/systemIds";
import AppointmentBuildingInformation from "@/components/appointment/appointmentBuildingInformation";
import {
  Breadcrumbs,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import DefaultInput from "@/components/forms/inputs/defaultInput";
import PeopleIcon from "@mui/icons-material/People";
import CircleIcon from "@mui/icons-material/Circle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import styles from "@/components/appointmentRequest/appointmentRequestBuildingInformation.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import DropDown from "@/components/forms/inputs/dropDown";
import type { AppointmentRequestEmailMode } from "@/types/Entities";
import { alpha } from "@mui/material/styles";

type AppointmentRequestType =
  | "standard_single"
  | "standard_multiple"
  | "single_company"
  | "multiple_companies";

const AppointmentRequestBuildingInformation: FC = () => {
  const { t } = useTranslation();
  const [buildingInformation, setBuildingInformation] = useState<any>();
  const [buildingOptionsForSubject, setBuildingOptionsForSubject] =
    useState<any>([]);
  const [selectedBuilding, setSelectedBuilding] = useState<any>();
  const [customTitel, setCustomTitle] = useState<string>("");
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [invitedSubjects, setInvitedSubjects] = useState<any>([]);
  const [executedBy, setExecutedBy] = useState<any>("");
  const [assistedBy, setAssistedBy] = useState<any>("");
  const [kaerUserOptions, setKaerUserOptions] = useState<any[]>([]);
  const [onSite, setOnsite] = useState<boolean | null>(null);
  const [emailMode, setEmailMode] =
    useState<AppointmentRequestEmailMode>("all_emails");
  const [emailModeHelpAnchor, setEmailModeHelpAnchor] =
    useState<HTMLElement | null>(null);

  const [formValues, setFormValues] = useState<any>({
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    parking_situation: "",
    additional_information: "",
    customer_information: "",
  });
  const [subjectId, setSubjectId] = useState<any>();
  const { handleError, errorModal } = useErrorModal();
  const [currentAppointmentRequest, setCurrentAppointmentRequest] =
    useState<any>();

  const { appointmentrequestId } = useParams() as {
    appointmentrequestId: string;
  };
  const navigate = useNavigate();

  const onSiteOptions = [
    { label: t("yes"), value: true },
    { label: t("no"), value: false },
  ];
  const emailModeOptions = (
    ["all_emails", "initial_only", "manual_only"] as const
  ).map((value) => ({
    value,
    label: t(`appointment_request_email_mode_${value}`),
  }));

  useEffect(() => {
    if (!appointmentrequestId) return;
    const loadIniitialData = async () => {
      const [users] = await Promise.all([getUsers(handleError)]);

      const userOptions = users
        .map((user: any) => {
          return {
            value: user.subject_id,
            label: user.last_name + ", " + user.first_name,
          };
        })
        .sort((a: any, b: any) => a.label.localeCompare(b.label));

      setKaerUserOptions(userOptions);
      setExecutedBy(currentAppointmentRequest?.executed_by?.id);
      setAssistedBy(currentAppointmentRequest?.assisted_by?.id);
      setOnsite(currentAppointmentRequest?.on_site);
    };
    loadIniitialData();
  }, [appointmentrequestId, currentAppointmentRequest]);
  useEffect(() => {
    if (!appointmentrequestId) {
      return;
    }

    api.genericApiRequest({
      entity: "appointmentRequest",
      method: "get",
      entityId: appointmentrequestId,
      parametersToRender: { depth: "1" },
      successHandler: (res: any) => {
        setCurrentAppointmentRequest(res.data);
        setEmailMode(res.data.email_mode ?? "all_emails");
        setBuildingInformation(res.data.building_information);
        const idForBuilding =
          (res.data.type as AppointmentRequestType) === "standard_single" ||
          (res.data.type as AppointmentRequestType) === "single_company"
            ? res.data?.company_subject_set[0].id
            : null;

        setSubjectId(idForBuilding);
        setSelectedBuilding(res.data?.building_information?.building);
        setBuildingInformation(res.data?.building_information);

        setCustomTitle(res.data.title);
        const tempInvited = [];
        for (const key in res.data.subject_info_set) {
          const subject = res.data.subject_info_set[key];
          tempInvited.push(subject);
          tempInvited.sort((a: any, b: any) => a.name.localeCompare(b.name));
        }
        setInvitedSubjects(tempInvited);

        setFormValues({
          contact_name:
            res.data.building_information.contact_name !== null
              ? res.data.building_information.contact_name
              : formValues.contact_name,
          contact_phone:
            res.data.building_information.contact_phone !== null
              ? res.data.building_information.contact_phone
              : formValues.contact_phone,
          contact_email:
            res.data.building_information.contact_email !== null
              ? res.data.building_information.contact_email
              : formValues.contact_email,
          parking_situation:
            res.data.building_information.parking_situation !== null
              ? res.data.building_information.parking_situation
              : formValues.parking_situation,
          additional_information:
            res.data.building_information.additional_information !== null
              ? res.data.building_information.additional_information
              : formValues.additional_information,
          customer_information:
            res.data.building_information.customer_information !== null
              ? res.data.building_information.customer_information
              : formValues.customer_information,
        });
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  }, [appointmentrequestId]);

  useEffect(() => {
    if (
      (currentAppointmentRequest?.type as AppointmentRequestType) !==
        "standard_single" &&
      (currentAppointmentRequest?.type as AppointmentRequestType) !==
        "single_company"
    )
      return;
    if (subjectId !== undefined && subjectId !== null) {
      const additionalUrlParameters: any = { subject: subjectId };
      api.genericApiRequest({
        entity: "building",
        method: "get",
        parametersToRender: {
          depth: "0",
          additionalUrlParameters: additionalUrlParameters,
        },
        successHandler: (res: any) => {
          const data = res.data.results;
          const tempBuildingOptions = [];

          for (const key in data) {
            const building = data[key];
            tempBuildingOptions.push({
              value: building.id,
              label:
                building.name +
                ` (${building.street_house}, ${building.zip} ${building.city})`,
              contact_name: building.contact_name,
              contact_email: building.contact_email,
              contact_phone: building.contact_phone,
              parking_situation: building.parking_situation,
              additional_information: building.additional_information,
            });
          }
          // handlebuildingFormValues(data);
          setBuildingOptionsForSubject(tempBuildingOptions);
        },
        failHandler: (error: any) => {
          handleError(error);
        },
      });
    }
  }, [subjectId]);

  const handleTitleChange = (event: any) => {
    event.preventDefault();
    setCustomTitle(event.target.value);
  };

  const submitBuildingInformation = () => {
    const result = {
      building: selectedBuilding,
      contact_name: formValues.contact_name,
      contact_phone: formValues.contact_phone,
      contact_email: formValues.contact_email,
      parking_situation: formValues.parking_situation,
      additional_information: formValues.additional_information,
      customer_information: formValues.customer_information,
    };

    return new Promise<void>((resolve, reject) => {
      api.genericApiRequest({
        entity: "buildingInformation",
        method: "put",
        entityId: buildingInformation.id,
        successHandler: () => resolve(),
        failHandler: reject,
        submitData: result,
      });
    });
  };

  const handleSubmit = async () => {
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    const result = {
      title: customTitel,
      executed_by: executedBy,
      assisted_by: assistedBy,
      ...(typeof onSite === "boolean" && { on_site: onSite }),
      ...(["draft", "open"].includes(currentAppointmentRequest.status) && {
        email_mode: emailMode,
      }),
      buildingInformation: buildingInformation.id,
    };
    try {
      await submitBuildingInformation();
      await new Promise<void>((resolve, reject) => {
        api.genericApiRequest({
          entity: "appointmentRequest",
          method: "put",
          entityId: appointmentrequestId,
          submitData: result,
          config: config,
          successHandler: () => resolve(),
          failHandler: reject,
        });
      });
      navigate(
        currentAppointmentRequest.status === "draft"
          ? `/editappointmentrequest/${appointmentrequestId}/calendar`
          : "/appointmentrequest"
      );
    } catch (error) {
      handleError(error);
    }
  };

  const handleInputChange = (event: any) => {
    const { name, value } = event.target;

    setFormValues((prevState: any) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleBuildingChange = (selectedOption: any) => {
    if (
      selectedOption &&
      typeof selectedOption === "object" &&
      selectedOption.value
    ) {
      setSelectedBuilding(selectedOption.value);
    }
  };

  const handleResponsibleUserChange = (
    selectedOption: any,
    fieldName: "executed_by" | "assisted_by" | "on_site"
  ) => {
    if (fieldName === "on_site") {
      if (selectedOption === undefined) return;
      if (selectedOption === null) {
        setOnsite(false);
        return;
      }
      if (typeof selectedOption === "object") {
        setOnsite(selectedOption.value);
      }
      return;
    }
    if (
      selectedOption &&
      typeof selectedOption === "object" &&
      selectedOption.value
    ) {
      if (fieldName === "executed_by") {
        setExecutedBy(selectedOption.value);
      } else setAssistedBy(selectedOption.value);
    }
  };

  const companyName = currentAppointmentRequest?.company_subject?.name;
  const heading = companyName
    ? `${t("building_information")} - ${companyName}`
    : t("building_information");
  usePageHeading(heading);

  if (!currentAppointmentRequest) {
    return null;
  }

  return (
    <>
      <Grid container className={styles.container} rowSpacing={"1rem"}>
        <Grid item xs={12}>
          {appointmentrequestId &&
            currentAppointmentRequest.status !== "draft" && (
              <div>
                <Breadcrumbs separator="|">
                  <Link
                    className={styles.linkNoUnderline}
                    to={`/editappointmentrequest/${currentAppointmentRequest.id}/settings`}
                  >
                    {t("basic_information")}
                  </Link>
                  <Link
                    className={styles.linkUnderlined}
                    to={`/editappointmentrequest/${currentAppointmentRequest.id}/building`}
                  >
                    {t("building_location")}
                  </Link>
                  <Link
                    className={styles.linkNoUnderline}
                    to={`/editappointmentrequest/${currentAppointmentRequest.id}/calendar`}
                  >
                    {t("period")}
                  </Link>
                </Breadcrumbs>
              </div>
            )}
          <div className={styles.heading}>
            <Tooltip title={t("show_invited")}>
              <GenericButton
                onClick={() => setModalOpen(true)}
                className={styles.showInvitedButton}
              >
                <PeopleIcon />
              </GenericButton>
            </Tooltip>
            {/* )} */}
          </div>
        </Grid>

        <Grid item xs={12}>
          <DefaultInput
            name="name"
            label={t("name")}
            value={customTitel}
            onChange={handleTitleChange}
            disabled={
              currentAppointmentRequest.status === "draft" ? true : false
            }
          />
        </Grid>
        <Grid item xs={12}>
          <DropDown
            dataTestId="appointment-request-building-responsible-field"
            defaultValue={executedBy}
            name="executed_by"
            label={t("executed_by")}
            options={kaerUserOptions}
            onChange={(selectedOption) =>
              handleResponsibleUserChange(selectedOption, "executed_by")
            }
            wrapperClassName={styles.dropDown__executedBy}
            disabled={
              currentAppointmentRequest.status === "draft" ? true : false
            }
          />
        </Grid>
        <Grid item xs={12}>
          <DropDown
            dataTestId="appointment-request-building-assistant-field"
            defaultValue={assistedBy}
            name="assisted_by"
            label={t("assisted_by")}
            options={kaerUserOptions}
            onChange={(selectedOption) =>
              handleResponsibleUserChange(selectedOption, "assisted_by")
            }
            wrapperClassName={styles.dropDown__assistedBy}
            disabled={
              currentAppointmentRequest.status === "draft" ? true : false
            }
          />
        </Grid>
        <Grid item xs={12}>
          <DropDown
            dataTestId="appointment-request-on-site-field"
            externalValue={
              onSiteOptions.find((option) => option.value === onSite) ?? null
            }
            name="on_site"
            label={t("on_site")}
            options={onSiteOptions}
            onChange={(selectedOption) =>
              handleResponsibleUserChange(selectedOption, "on_site")
            }
            isClearable={true}
            wrapperClassName={styles.dropDown__assistedBy}
          />
        </Grid>

        <Grid item xs={12}>
          <DropDown
            dataTestId="appointment-request-email-mode-field"
            name="email_mode"
            label={
              <>
                {t("appointment_request_email_mode")}
                <IconButton
                  size="small"
                  aria-label={t("appointment_request_email_mode_info")}
                  aria-expanded={Boolean(emailModeHelpAnchor)}
                  aria-haspopup="dialog"
                  aria-controls={
                    emailModeHelpAnchor
                      ? "appointment-request-email-mode-help"
                      : undefined
                  }
                  onClick={(event) =>
                    setEmailModeHelpAnchor(event.currentTarget)
                  }
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </>
            }
            labelStyle={{
              display: "flex",
              alignItems: "center",
              gap: "0.25rem",
            }}
            externalValue={emailModeOptions.find(
              (option) => option.value === emailMode
            )}
            options={emailModeOptions}
            onChange={(
              option: { value: AppointmentRequestEmailMode } | null
            ) => {
              if (option) setEmailMode(option.value);
            }}
            isClearable={false}
            disabled={
              !["draft", "open"].includes(currentAppointmentRequest.status)
            }
          />
          <Popover
            id="appointment-request-email-mode-help"
            open={Boolean(emailModeHelpAnchor)}
            anchorEl={emailModeHelpAnchor}
            onClose={() => setEmailModeHelpAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "bottom", horizontal: "left" }}
            slotProps={{
              paper: {
                role: "dialog",
                "aria-label": t("appointment_request_email_mode_info"),
                sx: (theme) => ({
                  px: 2,
                  py: 1,
                  ml: 1,
                  width: "min(36rem, calc(100vw - 32px))",
                  boxSizing: "border-box",
                  overflow: "visible",
                  backgroundColor: alpha(theme.palette.grey[700], 0.92),
                  color: theme.palette.common.white,
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    right: "100%",
                    bottom: 9,
                    width: 0,
                    height: 0,
                    borderTop: "6px solid transparent",
                    borderBottom: "6px solid transparent",
                    borderRight: `6px solid ${alpha(
                      theme.palette.grey[700],
                      0.92
                    )}`,
                  },
                }),
              },
            }}
          >
            <Typography
              component="p"
              variant="caption"
              sx={{ fontStyle: "italic" }}
            >
              {t("appointment_request_email_mode_help")}
            </Typography>
          </Popover>
        </Grid>

        <Grid container item xs={12}>
          <AppointmentBuildingInformation
            buildingOptions={buildingOptionsForSubject}
            formValues={formValues}
            withGrid={true}
            handleInputChange={handleInputChange}
            handleBuildingChange={handleBuildingChange}
            selectedBuilding={selectedBuilding}
            disableBuildingForMultiCompany={
              (currentAppointmentRequest?.type as AppointmentRequestType) !==
                "standard_single" &&
              (currentAppointmentRequest?.type as AppointmentRequestType) !==
                "single_company"
            }
          />
        </Grid>
        <Grid item xs={12}>
          {appointmentrequestId &&
            currentAppointmentRequest.status !== "open" && (
              <GenericButton
                variant="outlined"
                onClick={() =>
                  navigate(
                    `/editappointmentrequest/${currentAppointmentRequest.id}/settings`
                  )
                }
              >
                {t("back")}
              </GenericButton>
            )}

          <GenericButton
            data-testid="appointment-request-continue-to-date-button"
            className={styles.submitButton}
            onClick={handleSubmit}
            waitingForResponse={true}
            showSpinnerOnDisable={true}
          >
            {currentAppointmentRequest.status === "draft"
              ? t("continue_to_appointment_date_selection")
              : t("save")}
          </GenericButton>
        </Grid>
      </Grid>
      {errorModal}
      <Dialog
        open={modalOpen}
        scroll="paper"
        component={"div"}
        onClose={() => setModalOpen(false)}
      >
        <DialogTitle textAlign={"center"}>{t("invited_users")}</DialogTitle>
        <DialogContent dividers>
          <DialogContentText display={"flex"} flexDirection={"column"}>
            <List>
              {invitedSubjects.map((subject: any) => (
                <ListItem key={subject.id} disablePadding>
                  <ListItemIcon>
                    {subject.appointment_created ? (
                      <CircleIcon color="success" fontSize="small" />
                    ) : (
                      <CircleIcon color="error" fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText primary={subject.name} />
                </ListItem>
              ))}
            </List>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <GenericButton
            onClick={() => {
              setModalOpen(false);
            }}
          >
            {t("ok_capital")}
          </GenericButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AppointmentRequestBuildingInformation;

const getUsers = async (handleError: any) => {
  const additionalUserParameters: any = {};
  additionalUserParameters["impersonate_subject"] = KAER_COMPANY_SUBJECT_ID;
  additionalUserParameters["is_active"] = "true";

  try {
    const res: any = await new Promise<any>((resolve, reject) => {
      api.genericApiRequest({
        entity: "user",
        method: "get",
        parametersToRender: {
          additionalUrlParameters: additionalUserParameters,
          depth: "0",
        },

        successHandler: (res: any) => {
          resolve(res);
        },
        failHandler: (error: any) => {
          reject(error);
        },
      });
    });

    return res.data.results;
  } catch (error) {
    handleError(error);
    return [];
  }
};
